/**
 * What renders between the header and the bottom bar.
 *
 * The shell does NOT change which route matched — `routes.tsx` still generates
 * every route from `PAGE_REGISTRY` and `RequireAuth` still gates the pathname
 * before this component exists. All this decides is which COMPONENT draws the
 * inside of an already-guarded route:
 *
 *     /dashboard   ->  <WalletHome/>     if built, else the existing page
 *     /records     ->  <WalletRecords/>  if built, else the existing page
 *     /analytics   ->  <WalletStats/>    if built, else the existing page
 *     everything else                    the existing page
 *
 * EXACT MATCH ONLY. `/records/statements`, `/accounts/:id`, `/rent/payments`
 * and every other nested route keeps its real page — a Wallet screen replaces a
 * landing screen, never a whole section it was not written for.
 *
 * A missing screen is not an error state. `<Outlet/>` is the same element the
 * classic shell renders, so before any of the four packages land this shell is
 * simply the current app wearing Wallet's chrome — which is exactly what makes
 * it evaluable early.
 *
 * SCREEN TRANSITIONS
 * ------------------
 * Moving between the three primary tabs slides+fades directionally —
 * `screenDirection()` (pure, unit-tested beside this file) decides which way
 * from the tabs' left-to-right order, never from browser history, so a link
 * INTO the Wallet tabs from a classic page still picks a sensible direction.
 * Both the entering and exiting screen occupy the SAME grid cell (`grid-area:
 * 1 / 1`) rather than one being `position: absolute` — that keeps the
 * container's height following whichever screen is taller during the ~200ms
 * overlap instead of collapsing to zero, with no layout thrash: grid placement
 * is static, only the two children's own `transform`/`opacity` animate.
 *
 * Content is NEVER held back for the animation — the incoming screen mounts
 * immediately (Suspense still shows its own fallback if the chunk is still
 * loading); the transition only decorates how it arrives. Reduced motion
 * collapses the transition to an instant cut, per the shell's motion contract.
 */
import { Suspense, useMemo, useRef, type ComponentType } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Outlet, useLocation } from "react-router-dom";
import { HOME_HREF, RECORDS_HREF, STATS_HREF } from "./nav";
import { HomeScreen, RecordsScreen, StatsScreen } from "./screens";
import { screenDirection, type TabKey } from "./screenDirection";
import { SPRING_INSTANT, SPRING_SCREEN } from "@/lib/motion";

function screenFor(pathname: string): ComponentType<Record<string, never>> | null {
  if (pathname === HOME_HREF || pathname === "/") return HomeScreen;
  if (pathname === RECORDS_HREF) return RecordsScreen;
  if (pathname === STATS_HREF) return StatsScreen;
  return null;
}

function tabKeyFor(pathname: string): TabKey | null {
  if (pathname === HOME_HREF || pathname === "/") return "home";
  if (pathname === RECORDS_HREF) return "records";
  if (pathname === STATS_HREF) return "stats";
  return null;
}

function ScreenFallback() {
  return (
    <div className="space-y-3 p-4">
      <div className="h-24 animate-pulse rounded-[var(--cp-r-card)] bg-[var(--cp-surface-2)]" />
      <div className="h-40 animate-pulse rounded-[var(--cp-r-card)] bg-[var(--cp-surface-2)]" />
    </div>
  );
}

const SLIDE_PX = 24;

const variants = {
  enter: (direction: number) => ({
    x: direction === 0 ? 0 : direction * SLIDE_PX,
    opacity: 0,
  }),
  center: { x: 0, opacity: 1 },
  exit: (direction: number) => ({
    x: direction === 0 ? 0 : direction * -SLIDE_PX,
    opacity: 0,
  }),
};

export function ScreenSlot() {
  const { pathname } = useLocation();
  const reduceMotion = useReducedMotion();
  const Screen = screenFor(pathname);
  const tabKey = tabKeyFor(pathname);

  // Tracks the previously rendered tab so the direction can be computed
  // without depending on router history (a link INTO the tabs from a classic
  // page still gets a sensible direction, not "no direction").
  const previousTabRef = useRef<TabKey | null>(null);
  const direction = useMemo(() => {
    const dir = screenDirection(previousTabRef.current, tabKey);
    previousTabRef.current = tabKey;
    return dir;
  }, [tabKey]);

  const content = Screen ? (
    <Suspense fallback={<ScreenFallback />}>
      <Screen />
    </Suspense>
  ) : (
    <Outlet />
  );

  if (reduceMotion) {
    // Instant, no directional slide — the content still repaints on
    // navigation, it just does not animate getting there.
    return (
      <div key={pathname} className="min-w-0">
        {content}
      </div>
    );
  }

  return (
    <div className="grid min-w-0">
      <AnimatePresence initial={false} mode="sync" custom={direction}>
        <motion.div
          key={pathname}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={reduceMotion ? SPRING_INSTANT : SPRING_SCREEN}
          // `minWidth: 0` is load-bearing, not tidiness: a grid item defaults
          // to `min-width: auto`, so it is sized by its widest MIN-CONTENT
          // rather than by this column. One page whose content refuses to wrap
          // (the rent sub-nav's `shrink-0 whitespace-nowrap` row) then widened
          // the document itself and put a horizontal scrollbar under the whole
          // app. With this, such content scrolls inside its own container —
          // which is what those containers already asked for.
          style={{ gridArea: "1 / 1", minWidth: 0, willChange: "transform, opacity" }}
        >
          {content}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
